/**
 *
 * Converts a HEX color string to its HSL representation.
 *
 * @param hex - The HEX color string.
 * @returns An array containing the HSL representation [hue, saturation, lightness].
 *
 */

export function fromHexToHsl(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result?.length) {
    throw new Error("Invalid HEX color format");
  }

  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);

  (r /= 255), (g /= 255), (b /= 255);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max == min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  s = s * 100;
  s = Math.round(s);
  l = l * 100;
  l = Math.round(l);

  h = Math.round((h || 0) * 360);

  return [h, s, l];
}

// def hsv_to_rgb(h, s, v)
//   h_i = (h*6).to_i
//   f = h*6 - h_i
//   p = v * (1 - s)
//   q = v * (1 - f*s)
//   t = v * (1 - (1 - f) * s)
//   r, g, b = v, t, p if h_i==0
//   r, g, b = q, v, p if h_i==1
//   r, g, b = p, v, t if h_i==2
//   r, g, b = p, q, v if h_i==3
//   r, g, b = t, p, v if h_i==4
//   r, g, b = v, p, q if h_i==5
//   [(r*256).to_i, (g*256).to_i, (b*256).to_i]
// end

/**
 *
 * Converts HSV color values to RGB.
 * More at: https://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
 *
 * @param h   Hue component (0 to 1)
 * @param s   Saturation component (0 to 1)
 * @param v   Value component (0 to 1)
 * @returns    An array containing the RGB representation [red, green, blue].
 */
export function fromhsvToRgb(
  h: number,
  s: number,
  v: number
): [number, number, number] {
  const h_i = Math.floor(h * 6);
  const f = h * 6 - h_i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r: number, g: number, b: number;
  switch (h_i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
    default:
      r = 0;
      g = 0;
      b = 0;
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

const GOLDEN_RATIO_CONJUGATE = 0.618033988749895;

// h = rand # use random start value
// gen_html {
//   h += golden_ratio_conjugate
//   h %= 1
//   hsv_to_rgb(h, 0.5, 0.95)
// }

/**
 * Generates a distinct color using the golden ratio conjugate method.
 * More at: https://martin.ankerl.com/2009/12/09/how-to-create-random-colors-programmatically/
 * 
 * @param seedHue  Optional seed hue (0 to 1). If not provided, a random hue is used.
 * @param ratio  The ratio to increment the hue by, defaulting to the golden ratio conjugate.
 * @returns A distinct color in RGB format.
 */
export function generateDistinctColor(seedHue?: number, ratio: number = GOLDEN_RATIO_CONJUGATE): string {
  let h = seedHue ?? Math.random();
  h += ratio;
  h %= 1;
  const [r, g, b] = fromhsvToRgb(h, 0.5, 0.95);
  return `rgb(${r}, ${g}, ${b})`;
}
